if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.scale = 1;
        this.x = 0;
        this.y = 0;
        this.addEventListener('click', (event) => {
          const action = event.target.closest('[data-media-action]')?.dataset.mediaAction;
          if (action === 'previous') this.step(-1);
          if (action === 'next') this.step(1);
          if (action === 'in') this.zoom(this.scale + 0.5);
          if (action === 'out') this.zoom(this.scale - 0.5);
          if (action === 'reset') this.zoom(1);
        });
        this.addEventListener('keydown', (event) => {
          if (event.target.closest('deferred-media, product-model')) return;
          const direction = { ArrowLeft: [-40, 0], ArrowRight: [40, 0], ArrowUp: [0, -40], ArrowDown: [0, 40] }[event.key];
          if (direction && this.scale > 1) {
            event.preventDefault();
            this.x += direction[0];
            this.y += direction[1];
            this.paint();
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(event.key === 'ArrowLeft' ? -1 : 1);
          } else if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            this.zoom(this.scale + 0.5);
          } else if (event.key === '-' || event.key === '0') {
            event.preventDefault();
            this.zoom(event.key === '0' ? 1 : this.scale - 0.5);
          }
        });
        this.addEventListener('pointerdown', (event) => {
          if (this.scale === 1 || event.target !== this.activeMedia || event.button !== 0 || this.drag) return;
          event.preventDefault();
          this.drag = { id: event.pointerId, x: event.clientX - this.x, y: event.clientY - this.y };
          this.setPointerCapture(event.pointerId);
        });
        this.addEventListener('pointermove', (event) => {
          if (event.pointerId !== this.drag?.id) return;
          this.x = event.clientX - this.drag.x;
          this.y = event.clientY - this.drag.y;
          this.paint();
        });
        const stop = () => { this.drag = null; };
        this.addEventListener('pointerup', stop);
        this.addEventListener('pointercancel', stop);
        this.addEventListener('lostpointercapture', stop);
        this.addEventListener('dragstart', (event) => {
          if (event.target.tagName === 'IMG') event.preventDefault();
        });
        this.addEventListener('load', () => this.paint(), true);
      }

      connectedCallback() {
        super.connectedCallback();
        this.lifecycle?.abort();
        this.lifecycle = new AbortController();
        window.addEventListener('resize', () => this.zoom(1), { signal: this.lifecycle.signal });
        document.addEventListener('shopify:section:unload', (event) => {
          if (String(event.detail?.sectionId) !== this.dataset.section) return;
          if (this.hasAttribute('open')) this.hide();
          this.remove();
        }, { signal: this.lifecycle.signal });
      }

      disconnectedCallback() {
        this.lifecycle?.abort();
        if (this.hasAttribute('open')) this.hide();
      }

      get content() { return this.querySelector('.product-media-modal__content'); }
      get media() { return Array.from(this.content.querySelectorAll(':scope > [data-media-id]')); }
      get activeMedia() { return this.content.querySelector(':scope > .active'); }
      get navigableMedia() {
        return this.media.filter((item) => !item.classList.contains('product__media-item--variant') || item.dataset.mediaId === this.variantMediaId);
      }

      hide() {
        this.zoom(1);
        super.hide();
      }

      show(opener) {
        this.openedBy = opener;
        this.variantMediaId = opener.dataset.mediaId;
        this.activate(this.media.find((item) => item.dataset.mediaId === opener.dataset.mediaId) || this.media[0]);
        super.show(opener);
        this.paint();
        trapFocus(this, this.querySelector('[id^="ModalClose-"]'));
      }

      showActiveMedia() {
        this.activate(this.media.find((item) => item.dataset.mediaId === this.openedBy?.dataset.mediaId) || this.media[0]);
      }

      activate(media) {
        if (!media) return;
        window.pauseAllMedia();
        this.media.forEach((item) => {
          const active = item === media;
          item.classList.toggle('active', active);
          item.hidden = !active;
          item.style.transform = '';
        });
        this.zoom(1);
        this.content.scrollTop = 0;
        this.content.scrollLeft = 0;
        if (this.hasAttribute('open')) trapFocus(this, this.contains(document.activeElement) ? document.activeElement : this.querySelector('[id^="ModalClose-"]'));
        const items = this.navigableMedia;
        this.querySelectorAll('[data-media-action="previous"], [data-media-action="next"]').forEach((button) => {
          button.disabled = items.length < 2;
        });
        const counter = this.querySelector('[data-media-count]');
        if (counter) counter.textContent = `${items.indexOf(media) + 1} / ${items.length}`;
        if (media.nodeName === 'DEFERRED-MEDIA' && media.querySelector('template')?.content.querySelector('.js-youtube')) {
          media.loadContent();
        }
      }

      step(direction) {
        const items = this.navigableMedia;
        if (items.length < 2) return;
        this.activate(items[(items.indexOf(this.activeMedia) + direction + items.length) % items.length]);
      }

      zoom(scale) {
        this.scale = this.activeMedia?.tagName === 'IMG' ? Math.max(1, Math.min(3, scale)) : 1;
        if (this.scale === 1) { this.x = 0; this.y = 0; }
        this.drag = null;
        this.paint();
      }

      paint() {
        const focusedControl = this.contains(document.activeElement) ? document.activeElement : null;
        const media = this.activeMedia;
        if (!media) return;
        const image = media.tagName === 'IMG';
        const maxX = image ? Math.max(0, (media.offsetWidth * this.scale - this.content.clientWidth) / 2) : 0;
        const maxY = image ? Math.max(0, (media.offsetHeight * this.scale - this.content.clientHeight) / 2) : 0;
        this.x = Math.max(-maxX, Math.min(maxX, this.x));
        this.y = Math.max(-maxY, Math.min(maxY, this.y));
        if (image && media.offsetWidth) media.sizes = `${Math.ceil(media.offsetWidth * this.scale)}px`;
        if (image) media.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
        this.content.classList.toggle('is-zoomed', image && this.scale > 1);
        this.querySelectorAll('[data-media-action]').forEach((button) => {
          const action = button.dataset.mediaAction;
          if (action === 'in') button.disabled = !image || this.scale >= 3;
          if (action === 'out' || action === 'reset') button.disabled = !image || this.scale <= 1;
        });
        if (focusedControl?.disabled) this.content.focus({ preventScroll: true });
        const status = this.querySelector('[data-media-zoom]');
        if (status) status.textContent = `${Math.round(this.scale * 100)}%`;
      }
    }
  );
}
